import { useState, useEffect } from 'react';
import { X, Plus, Share, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Detects if the app is running in standalone mode (installed as PWA)
 */
const isStandalone = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
};

/**
 * Detects if the device is iOS
 */
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Detects if the device is Android
 */
const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Add to Home Screen prompt component
 * Shows installation instructions for iOS or uses the native prompt for Android/Chrome
 */
const AddToHomeScreen = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed this session
    if (isStandalone()) return;
    
    // Check if user has dismissed before (stored in localStorage)
    const dismissedAt = localStorage.getItem('equipr_pwa_dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // For Android/Chrome, listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show the custom instructions after a delay
    if (isIOS()) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // For Android/Chrome - use the native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('equipr_pwa_dismissed', new Date().toISOString());
  };

  if (!showPrompt || dismissed || isStandalone()) return null;

  // iOS Instructions
  if (isIOS()) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-6 bg-zinc-900 border-t border-zinc-800 animate-in slide-in-from-bottom duration-300">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#ffd108] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-black font-bold text-xl">E</span>
          </div>
          <div className="flex-1 pr-6">
            <h3 className="font-semibold text-white">Add EQUIPR CORE to Home Screen</h3>
            <p className="text-sm text-zinc-400 mt-1">
              Install this app on your device for quick access - no app store needed!
            </p>
            <div className="mt-3 flex items-center gap-2 text-sm text-zinc-300">
              <span>Tap</span>
              <Share className="h-5 w-5 text-[#007AFF]" />
              <span>then</span>
              <span className="flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded">
                <Plus className="h-4 w-4" />
                Add to Home Screen
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mt-4">
          <ChevronUp className="h-5 w-5 text-zinc-500 animate-bounce" />
        </div>
      </div>
    );
  }

  // Android / Chrome Instructions
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-zinc-900 border-t border-zinc-800 animate-in slide-in-from-bottom duration-300">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>
      
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#ffd108] rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-black font-bold text-xl">E</span>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-white">Add EQUIPR to Home Screen</h3>
          <p className="text-sm text-zinc-400 mt-0.5">
            Quick access without remembering the URL
          </p>
        </div>
        <Button 
          onClick={handleInstall}
          className="bg-[#ffd108] text-black hover:bg-[#e6bc00] font-semibold"
        >
          Install
        </Button>
      </div>
    </div>
  );
};

export default AddToHomeScreen;
